/**
 * Configuración de textos y colores para el módulo OrderPreparation
 *
 * Este archivo centraliza toda la lógica relacionada con:
 * - Nombres de tipos de preparación
 * - Nombres de estados de preparación
 * - Colores de badges según estado
 * - Textos de botones según estado
 * - Colores de botones según estado
 *
 * @author Equipo de desarrollo
 * @version 1.0.0
 */

import { useTranslation } from 'react-i18next';

// Enums para tipos y estados
export enum PreparationType {
  MASSIVE_PACKING = 1,
  PACKING = 2,
  PICKING = 3,
}

export enum PreparationStatus {
  CANCELLED = 0,
  TO_PREPARE = 1,
  IN_PREPARATION = 2,
  COMPLETE = 3,
}

/**
 * Hook para obtener el nombre del tipo de preparación según el ID
 * @param type - ID del tipo de preparación
 * @returns Nombre del tipo de preparación
 */
export const usePreparationTypeName = (type: number): string => {
  const { t } = useTranslation('orderpreparationModule');

  switch (type) {
    case PreparationType.PICKING:
      return t('filters.options.type.1');
    case PreparationType.PACKING:
      return t('filters.options.type.2');
    case PreparationType.MASSIVE_PACKING:
      return t('filters.options.type.3');
    default:
      return t('fallbacks.no_preparation');
  }
};

/**
 * Hook para obtener el nombre del estado de preparación según el ID
 * @param status - ID del estado de preparación
 * @returns Nombre del estado de preparación
 */
export const usePreparationStatusName = (status: number): string => {
  const { t } = useTranslation('orderpreparationModule');

  switch (status) {
    case PreparationStatus.CANCELLED:
      return t('filters.options.status.0');
    case PreparationStatus.TO_PREPARE:
      return t('filters.options.status.1');
    case PreparationStatus.IN_PREPARATION:
      return t('filters.options.status.2');
    case PreparationStatus.COMPLETE:
      return t('filters.options.status.3');
    default:
      return t('fallbacks.no_preparation');
  }
};

/**
 * Obtiene el color del texto del badge según el estado
 * @param status - ID del estado de preparación
 * @returns Clase CSS para el color del texto
 */
export const getPreparationStatusColor = (status: number): string => {
  switch (status) {
    case PreparationStatus.CANCELLED:
      return 'text-error light:bg-bg-error'; // Rojo
    case PreparationStatus.TO_PREPARE:
      return 'text-badge-status-3 light:bg-badge-bg-status-3'; // Púrpura
    case PreparationStatus.IN_PREPARATION:
      return 'text-warning light:bg-bg-warning'; // Naranja
    case PreparationStatus.COMPLETE:
      return 'text-success light:bg-bg-success'; // Verde
    default:
      return 'text-badge-status-3';
  }
};

/**
 * Hook para obtener el texto del botón de acción según el estado
 * @param status - ID del estado de preparación
 * @returns Texto del botón
 */
export const useActionButtonText = (status: number): string => {
  const { t } = useTranslation('orderpreparationModule');

  switch (status) {
    case PreparationStatus.CANCELLED:
      return t('buttons.view');
    case PreparationStatus.TO_PREPARE:
    case PreparationStatus.IN_PREPARATION:
      return t('buttons.prepare');
    case PreparationStatus.COMPLETE:
      return t('buttons.view');
    default:
      return t('buttons.view');
  }
};

/**
 * Obtiene el color del botón de acción según el estado
 * @param status - ID del estado de preparación
 * @returns Clase CSS para el color del botón
 */
export const getActionButtonColor = (status: number): string => {
  switch (status) {
    case PreparationStatus.CANCELLED:
      return 'bg-card-border text-title hover:bg-hover-press';
    case PreparationStatus.TO_PREPARE:
    case PreparationStatus.IN_PREPARATION:
      return 'bg-primary text-btn-text hover:bg-hover-press';
    case PreparationStatus.COMPLETE:
      return 'bg-card-border text-title hover:bg-hover-press';
    default:
      return 'bg-card-border text-title hover:bg-hover-press';
  }
};
